import { ReactNode } from 'react';
import classnames from 'classnames';
import { createUseStyles, useTheme } from 'react-jss';

import { Theme } from '@app/theme';
import { Button, SquareButton, ThreeDotsSpinner } from 'components';
import { t } from 'i18next';

interface Props {
  title: ReactNode;
  loading?: boolean;
  generating?: boolean;
  loadingLabel?: ReactNode;
  className?: string;
  children?: ReactNode;
  displayed?: number;
  attempts?: number;
  changeDisplayed?: (index: number) => void;
  onRetry: () => void;
}

const useStyles = createUseStyles((theme: Theme) => ({
  container: {
    backgroundColor: theme.colors.grayBackground,
    borderRadius: theme.borderRadius.std,
    padding: theme.marginBase * 2,

    '& .dot-elastic, & .dot-elastic::before, & .dot-elastic::after': {
      backgroundColor: theme.colors.accentAccessible,
    },
  },
  header: {
    ...theme.fonts.label,
    display: 'flex',
    color: theme.colors.accentAccessible,
    marginBottom: theme.marginBase,
    textAlign: 'center',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  buttons: {
    display: 'flex',
    flexDirection: 'row',
    gap: theme.marginBase,
  },
  retryButton: {
    paddingInline: 0,
  },
  icon: {
    fontSize: theme.iconSize.small,
    lineHeight: '100%',
    marginRight: theme.marginBase,
  },
  buttonIcon: {
    fontSize: theme.iconSize.small,
    lineHeight: '100%',
    marginRight: theme.marginBase,
  },
  attempts: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.marginBase,
  },
  attemptCount: {
    ...theme.fonts.caption,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: theme.colors.inkSecondary,
    minWidth: theme.marginBase * 5,
    textAlign: 'center',
  },
}));

export const IaSuggestionBox = ({
  title,
  loading,
  generating,
  loadingLabel,
  className,
  children,
  displayed = 0,
  attempts = 0,
  changeDisplayed,
  onRetry,
}: Props) => {
  const theme: Theme = useTheme();
  const classes = useStyles({ theme });
  const retrying = generating ?? loading;

  return (
    <div className={classnames(classes.container, className)}>
      <div className={classes.header}>
        <div>
          <i className={classnames('ri-sparkling-2-line', classes.icon)} />
          {loading ? loadingLabel || title : title}
        </div>
        <div className={classes.buttons}>
          {attempts > 1 && (
            <>
              <SquareButton
                icon="ri-arrow-left-s-line"
                color="accent"
                variant="muted"
                disabled={displayed === 0}
                small
                onClick={() => changeDisplayed?.(displayed - 1)}
              />
              <span className={classes.attemptCount}>
                {displayed + 1} / {attempts}
              </span>
              <SquareButton
                icon="ri-arrow-right-s-line"
                color="accent"
                variant="muted"
                small
                disabled={displayed >= attempts - 1}
                onClick={() => changeDisplayed?.(displayed + 1)}
              />
            </>
          )}
          {attempts < 3 && (
            <Button
              variant="transparent"
              color="accent"
              xsmall
              onClick={onRetry}
              isLoading={retrying}
              disabled={retrying}
              contentClassName={classes.retryButton}
            >
              <i
                className={classnames('ri-refresh-line', classes.buttonIcon)}
              />
              {t('editSession.screening.iaSuggestion.retry')}
            </Button>
          )}
        </div>
      </div>
      {!loading && children}
    </div>
  );
};
